import React from "react";
import { Button, Grid, FormControlLabel, Checkbox, Box, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { BaseDrawer } from "components/Shared/Base/BaseDrawer";
import LoadingWrapper from "components/Shared/LoadingWrapper";
import InnerShadowBox from "components/Shared/InnerShadowBox";
import { BASE_CONSTANTS } from "constants/baseConstants";
import BaseTextField from "components/Shared/Base/BaseTextField";
import { useAuth } from "hooks";
import { useVendorProfileDetailContext } from "contexts/VendorProfileDetailContext";

const ShippingRateForm = ({
    isBusy = false,
    shippingRateData = null,
    onSubmit = () => { },
    onCancel = () => { },
    vendorId = null,
}) => {
    const { viewEntity } = useAuth();
    const { vendorProfileDetail } = useVendorProfileDetailContext();

    const isEditMode = !!shippingRateData;

    const { control, handleSubmit, reset, watch, formState: { errors } } = useForm({
        defaultValues: {
            shippingRate: "",
            isActive: true, // Always default to true when adding new
        },
    });

    const isActive = watch("isActive");

    // Reset form when shippingRateData changes (edit mode)
    React.useEffect(() => {
        if (shippingRateData) {
            reset({
                shippingRate: shippingRateData.shippingRate?.toString() || "",
                isActive: shippingRateData.isActive ?? false,
            });
        } else {
            reset({
                shippingRate: "",
                isActive: true, // New rate → always active by default
            });
        }
    }, [shippingRateData, reset]);

    const handleOnSubmit = (data) => {
        const payload = {
            vendorId: vendorProfileDetail?._id || vendorId,
            shippingRate: Number(data.shippingRate),
            isActive: Boolean(data.isActive), // Ensure boolean
            entity: viewEntity?.entity?._id,
            account: viewEntity?.entity?.account?._id,
        };
        onSubmit(payload);
    };

    const handleCancel = () => {
        reset();
        onCancel();
    };

    return (
        <LoadingWrapper loading={isBusy}>
            <InnerShadowBox>
                <BaseDrawer.Content>
                    <Grid container spacing={BASE_CONSTANTS.SPACING_LG}>
                        {/* Shipping Rate Input */}
                        <Grid item xs={12}>
                            <Controller
                                name="shippingRate"
                                control={control}
                                rules={{
                                    required: "Shipping rate is required",
                                    min: { value: 0, message: "Rate cannot be negative" }
                                }}
                                render={({ field }) => (
                                    <BaseTextField
                                        {...field}
                                        required
                                        fullWidth
                                        label="Shipping Rate (Rs)"
                                        type="number"
                                        inputProps={{
                                            min: 0,
                                            step: "0.01",
                                        }}
                                        error={!!errors.shippingRate}
                                        helperText={errors.shippingRate?.message}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Active Rate Checkbox */}
                        <Grid item xs={12}>
                            <Controller
                                name="isActive"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                {...field}
                                                checked={field.value}
                                                onChange={(e) => field.onChange(e.target.checked)}
                                                color="primary"
                                                size="medium"
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography fontWeight={500}>
                                                    Active Rate
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {isEditMode ? (
                                                        isActive ? (
                                                            <>This rate will be active. All other rates will be deactivated.</>
                                                        ) : (
                                                            <>This rate is currently inactive.</>
                                                        )
                                                    ) : (
                                                        <>New rate will be set as active. All existing rates will be deactivated.</>
                                                    )}
                                                </Typography>
                                            </Box>
                                        }
                                        sx={{ alignItems: "flex-start", mt: 1 }}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </BaseDrawer.Content>
            </InnerShadowBox>

            {/* Action Buttons */}
            <BaseDrawer.Actions>
                <Button
                    onClick={handleCancel}
                    disabled={isBusy}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit(handleOnSubmit)}
                    disabled={isBusy}
                >
                    {isEditMode ? "Update" : "Add"} Rate
                </Button>
            </BaseDrawer.Actions>
        </LoadingWrapper>
    );
};

export default ShippingRateForm;